import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';

import {
  CalendlyImportStatus,
  useCalendlyImportStatusQuery,
  useImportCalendlyDataMutation,
  UpcomingScheduledEventsDocument,
  CalendarScheduledEventsDocument,
} from '../../../generated/graphql';
import Button from '../Button';
import ToastAlert from '../ToastAlert';
import { useAuth } from '../../../contexts/AuthContext';

const ImportStatusToast: React.FC = () => {
  const [successToastVisible, setSuccessToastVisible] = useState(false);

  const client = useApolloClient();
  const { authedProviderUser } = useAuth();
  const calendlyIntegration = authedProviderUser?.provider.calendlyIntegration;
  const isCalendlyEnabled = calendlyIntegration?.isEnabled ?? true;
  const hasCalendlyIntegrationAndEnabled = Boolean(
    calendlyIntegration && isCalendlyEnabled,
  );

  const previousImportStatus = useRef<CalendlyImportStatus | null>();

  const {
    data: importStatusData,
    loading: isImportStatusLoading,
    error: importStatusError,
    refetch: refetchCalendlyImportStatus,
  } = useCalendlyImportStatusQuery({
    skip: !hasCalendlyIntegrationAndEnabled,
    onCompleted: async (data) => {
      if (
        data.calendlyImportStatus === CalendlyImportStatus.Completed &&
        previousImportStatus.current &&
        previousImportStatus.current !== CalendlyImportStatus.Completed
      ) {
        setSuccessToastVisible(true);
        await client.refetchQueries({
          include: [
            UpcomingScheduledEventsDocument,
            CalendarScheduledEventsDocument,
          ],
        });
      }

      previousImportStatus.current = data.calendlyImportStatus;
    },
  });
  const [importCalendlyDataMutation, { loading: importCalendlyDataLoading }] =
    useImportCalendlyDataMutation();

  const navigate = useNavigate();

  const timeout = useRef<NodeJS.Timeout>();
  const shouldPollRef = useRef(false);
  // Tracks whether this component instance may start/continue polling.
  // Cleared on unmount or when the Calendly integration becomes inactive so
  // in-flight mutation/refetch continuations cannot revive polling.
  const isActiveRef = useRef(false);

  const clearPollTimeout = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = undefined;
    }
  };

  const pollNextImportStatus = () => {
    clearPollTimeout();

    timeout.current = setTimeout(async () => {
      if (!shouldPollRef.current || !isActiveRef.current) {
        return;
      }

      try {
        const { data } = await refetchCalendlyImportStatus();
        if (
          shouldPollRef.current &&
          isActiveRef.current &&
          data?.calendlyImportStatus === CalendlyImportStatus.Started
        ) {
          // Poll again if the status is still "Started"
          pollNextImportStatus();
        }
      } catch {
        // Retry transient status-query failures while polling is still active
        if (shouldPollRef.current && isActiveRef.current) {
          pollNextImportStatus();
        }
      }
    }, 5000);
  };

  useEffect(() => {
    isActiveRef.current = hasCalendlyIntegrationAndEnabled;

    const isImportStarted =
      hasCalendlyIntegrationAndEnabled &&
      importStatusData?.calendlyImportStatus === CalendlyImportStatus.Started;

    shouldPollRef.current = isImportStarted;

    if (isImportStarted) {
      pollNextImportStatus();
    } else if (
      hasCalendlyIntegrationAndEnabled &&
      importStatusData?.calendlyImportStatus === CalendlyImportStatus.NotStarted
    ) {
      importCalendlyData();
    }

    return () => {
      isActiveRef.current = false;
      shouldPollRef.current = false;
      clearPollTimeout();
    };
  }, [
    importStatusData?.calendlyImportStatus,
    hasCalendlyIntegrationAndEnabled,
  ]);

  const importCalendlyData = () => {
    importCalendlyDataMutation()
      .then(() => {
        // Do not revive polling after unmount or integration disablement.
        if (!isActiveRef.current) {
          return;
        }

        refetchCalendlyImportStatus().catch(() => {
          // Non-blocking: the polling request will retry the status query
        });
        shouldPollRef.current = true;
        pollNextImportStatus();
      })
      .catch(() => {
        // Non-blocking: import failure is surfaced by the status query
      });
  };

  if (
    isImportStatusLoading ||
    importStatusData?.calendlyImportStatus ===
      CalendlyImportStatus.NotStarted ||
    (importStatusData?.calendlyImportStatus ===
      CalendlyImportStatus.Completed &&
      !successToastVisible)
  ) {
    return <></>;
  }

  return (
    <div className="fixed bottom-3 left-0 right-0 flex justify-center">
      {importStatusData?.calendlyImportStatus ===
        CalendlyImportStatus.Started && (
        <ToastAlert
          level="info"
          message="We’re importing your data from Calendly. This should only take a minute or two."
          isVisible
          isLoading
        />
      )}
      {importStatusData?.calendlyImportStatus ===
        CalendlyImportStatus.Completed && (
        <ToastAlert
          level="success"
          message="Your data has been imported from Calendly."
          isVisible
          rightElements={
            <>
              <Button
                title="View your schedule"
                noOutline
                noBackground
                theme="secondary"
                size="small"
                className="font-bold text-green-150"
                onClick={() => {
                  navigate('/calendar');
                  setSuccessToastVisible(false);
                }}
              />

              <Button
                title="Dismiss"
                noOutline
                noBackground
                theme="secondary"
                size="small"
                className="ml-3 font-bold text-green-150"
                onClick={() => setSuccessToastVisible(false)}
              />
            </>
          }
        />
      )}
      {importStatusData?.calendlyImportStatus ===
        CalendlyImportStatus.Failed && (
        <ToastAlert
          level="error"
          message="Failed to import your data from Calendly."
          isVisible
          rightElements={
            <Button
              title="Retry"
              noOutline
              noBackground
              theme="secondary"
              size="small"
              className="font-bold text-green-150"
              onClick={importCalendlyData}
              disabled={importCalendlyDataLoading}
            />
          }
        />
      )}
      {importStatusError && hasCalendlyIntegrationAndEnabled && (
        <ToastAlert
          level="error"
          message="Couldn't get the status of the import of your Calendly data."
          isVisible
          rightElements={
            <Button
              title="Retry"
              noOutline
              noBackground
              theme="secondary"
              size="small"
              className="font-bold text-green-150"
              onClick={() => refetchCalendlyImportStatus()}
              disabled={isImportStatusLoading}
            />
          }
        />
      )}
    </div>
  );
};

export default ImportStatusToast;
